import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
import {
  coordinateKeys,
  coordinates,
  exact,
  positive,
  record,
  sameTarget,
  target,
  targetKeys,
  text,
  type EzremsBindingCandidate,
  type EzremsBindingSelection,
  type EzremsBindingTarget,
} from './ezrems-binding-context';

/**
 * Unit별 EZREMS 연결 — 로컬 BE candidate(010, 미배포)의 OpenAPI·source 기준 계약이다.
 * Unit POST는 최초 연결(expected_binding_revision 0)만 받으며 재설정 모드가 없다.
 */
export interface EzremsUnitBindingScope {
  property_id: string;
  room_type_id: string;
  room_id: string;
  room_topology_revision: number;
  room_version: number;
  property_version: number;
  type_version: number;
}
export interface EzremsUnitBindingContext {
  unit_id: string;
  unit_version: number;
  room_id: string;
  room_version: number;
  /** 현재 Unit 연결 차수; 0이면 미연결. */
  binding_revision: number;
  binding: (EzremsBindingTarget & Omit<EzremsBindingCandidate, 'sales_kind'>) | null;
  scope: EzremsUnitBindingScope;
  unit_ids: string[];
  targets: EzremsBindingTarget[];
  status: 'NOT_SELECTED' | 'SUCCEEDED';
  branch_id?: number | null;
  branch_code?: string | null;
  project_code?: string | null;
  candidates: EzremsBindingCandidate[];
  next_resource_id: number | null;
}
export interface EzremsUnitBindingCommand {
  command_id: string;
  expected_unit_version: number;
  expected_binding_revision: 0;
  scope: EzremsUnitBindingScope;
  branch_id: number;
  branch_code: string;
  project_code: string;
  resource_id: number;
  dong: string;
  floor: string;
  ho2: string;
  reason: string;
}
export interface EzremsUnitBindingReceipt {
  command_id: string;
  unit_id: string;
  unit_version: number;
  binding_revision: 1;
  http_status: 200;
}

const scopeKeys = [
  'property_id',
  'room_type_id',
  'room_id',
  'room_topology_revision',
  'room_version',
  'property_version',
  'type_version',
] as const;
const scopeRevisions = [
  'room_topology_revision',
  'room_version',
  'property_version',
  'type_version',
] as const;
const commandKeys = [
  'command_id',
  'expected_unit_version',
  'expected_binding_revision',
  'scope',
  'branch_id',
  'branch_code',
  'project_code',
  'resource_id',
  'dong',
  'floor',
  'ho2',
  'reason',
] as const;
const receiptKeys = [
  'command_id',
  'unit_id',
  'unit_version',
  'binding_revision',
  'http_status',
] as const;

const invalidContext = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '현재 슬롯의 EZREMS 연결 정보와 후보를 확인하지 못했습니다.',
  });
const invalidCommand = (status?: number) =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    status,
    message: 'EZREMS 슬롯 연결 요청 또는 응답을 확인하지 못했습니다.',
  });

const validScope = (value: unknown): value is EzremsUnitBindingScope =>
  record(value) &&
  exact(value, scopeKeys) &&
  isUuid(value.property_id) &&
  isUuid(value.room_type_id) &&
  isUuid(value.room_id) &&
  scopeRevisions.every((key) => positive(value[key]));

function parseContext(
  value: unknown,
  unitId: string,
  selection?: EzremsBindingSelection,
): EzremsUnitBindingContext {
  if (
    !record(value) ||
    !exact(value, [
      'unit_id',
      'unit_version',
      'room_id',
      'room_version',
      'binding_revision',
      'binding',
      'scope',
      'unit_ids',
      'targets',
      'status',
      ...targetKeys.filter((key) => Object.hasOwn(value, key)),
      'candidates',
      'next_resource_id',
    ]) ||
    !isUuid(value.unit_id) ||
    value.unit_id.toLowerCase() !== unitId.toLowerCase() ||
    !positive(value.unit_version) ||
    !isUuid(value.room_id) ||
    !positive(value.room_version) ||
    !validScope(value.scope) ||
    value.scope.room_id.toLowerCase() !== value.room_id.toLowerCase() ||
    value.scope.room_version !== value.room_version ||
    !Number.isSafeInteger(value.binding_revision) ||
    (value.binding_revision as number) < 0 ||
    !Array.isArray(value.unit_ids) ||
    !value.unit_ids.every(isUuid) ||
    !value.unit_ids.some((id: string) => id.toLowerCase() === unitId.toLowerCase()) ||
    new Set(value.unit_ids.map((id: string) => id.toLowerCase())).size !== value.unit_ids.length ||
    !Array.isArray(value.targets) ||
    !value.targets.every(target) ||
    new Set(value.targets.map((t) => JSON.stringify([t.branch_id, t.project_code]))).size !==
      value.targets.length ||
    !Array.isArray(value.candidates) ||
    value.candidates.length > 100 ||
    value.candidates.some(
      (c: unknown) =>
        !record(c) ||
        !exact(c, [...coordinateKeys, 'sales_kind']) ||
        !coordinates(c) ||
        typeof c.sales_kind !== 'string',
    )
  )
    throw invalidContext();
  if (
    value.binding_revision === 0
      ? value.binding !== null
      : !record(value.binding) ||
        !exact(value.binding, [...targetKeys, ...coordinateKeys]) ||
        !coordinates(value.binding) ||
        !positive(value.binding.branch_id) ||
        !text(value.binding.branch_code) ||
        !text(value.binding.project_code)
  )
    throw invalidContext();
  const data = value as unknown as EzremsUnitBindingContext;
  if (selection === undefined) {
    if (
      data.status !== 'NOT_SELECTED' ||
      data.candidates.length ||
      data.next_resource_id !== null ||
      data.branch_id != null ||
      data.branch_code != null ||
      data.project_code != null
    )
      throw invalidContext();
    return data;
  }
  if (
    data.status !== 'SUCCEEDED' ||
    data.branch_id !== selection.branch_id ||
    data.branch_code !== selection.branch_code ||
    data.project_code !== selection.project_code ||
    !data.targets.some((t) => sameTarget(t, selection))
  )
    throw invalidContext();
  let previous = selection.after_resource_id ?? 0;
  for (const candidate of data.candidates) {
    if (candidate.resource_id <= previous) throw invalidContext();
    previous = candidate.resource_id;
  }
  if (
    data.next_resource_id !== null &&
    (!positive(data.next_resource_id) ||
      data.candidates.length !== 100 ||
      data.next_resource_id !== previous)
  )
    throw invalidContext();
  return data;
}

/**
 * 같은 후보 조회 query(branch_id·project_code·after_resource_id)를 Room과 공유한다.
 * OpenAPI는 Unit GET의 query를 선언하지 않지만 BE source는 같은 strict query를 받는다.
 */
export async function readEzremsUnitBindingContext(
  client: ServiceClient,
  unitId: string,
  selection?: EzremsBindingSelection,
  signal?: AbortSignal,
): Promise<EzremsUnitBindingContext> {
  if (
    !isUuid(unitId) ||
    (selection &&
      (!target({
        branch_id: selection.branch_id,
        branch_code: selection.branch_code,
        project_code: selection.project_code,
      }) ||
        (selection.after_resource_id !== undefined && !positive(selection.after_resource_id))))
  )
    throw invalidContext();
  const value = await client.get<unknown>(`admin/units/${unitId}/ezrems-binding-context`, {
    headers: { 'Cache-Control': 'no-store' },
    signal,
    ...(selection
      ? {
          params: {
            branch_id: selection.branch_id,
            project_code: selection.project_code,
            ...(selection.after_resource_id === undefined
              ? {}
              : { after_resource_id: selection.after_resource_id }),
          },
        }
      : {}),
  });
  return parseContext(value, unitId, selection);
}

export function validateEzremsUnitBindingCommand(unitId: unknown, command: unknown): void {
  const scope: unknown = record(command) ? command.scope : undefined;
  if (
    !isUuid(unitId) ||
    !record(command) ||
    !exact(command, commandKeys) ||
    !isUuid(command.command_id) ||
    !positive(command.expected_unit_version) ||
    command.expected_binding_revision !== 0 ||
    !validScope(scope) ||
    !positive(command.branch_id) ||
    !positive(command.resource_id) ||
    ![command.branch_code, command.project_code, command.dong, command.floor, command.ho2].every(
      text,
    ) ||
    typeof command.reason !== 'string' ||
    command.reason.trim().length === 0 ||
    Array.from(command.reason).length > 500
  )
    throw invalidCommand();
}

export function parseEzremsUnitBindingReceipt(
  value: unknown,
  unitId: string,
  command: EzremsUnitBindingCommand,
): EzremsUnitBindingReceipt {
  if (
    !record(value) ||
    !exact(value, receiptKeys) ||
    !isUuid(value.command_id) ||
    value.command_id.toLowerCase() !== command.command_id.toLowerCase() ||
    !isUuid(value.unit_id) ||
    value.unit_id.toLowerCase() !== unitId.toLowerCase() ||
    // BE repository는 결과 unit_version을 expected_unit_version + 1로 고정하고, 원 UUID 재생도 그 원 결과를 돌려준다.
    value.unit_version !== command.expected_unit_version + 1 ||
    value.binding_revision !== 1 ||
    value.http_status !== 200
  )
    throw invalidCommand(200);
  return value as unknown as EzremsUnitBindingReceipt;
}

/** One explicit command only. A failed/uncertain response must never trigger automatic replay. */
export async function bindEzremsUnit(
  client: ServiceClient,
  unitId: string,
  command: EzremsUnitBindingCommand,
): Promise<EzremsUnitBindingReceipt> {
  validateEzremsUnitBindingCommand(unitId, command);
  const value = await client.post<unknown>(`admin/units/${unitId}/ezrems-binding`, {
    json: command,
  });
  return parseEzremsUnitBindingReceipt(value, unitId, command);
}
